import { useState, useCallback } from 'preact/hooks';
import { api, type FarmResponse, type GpuInfo } from '../services/api.js';

export interface ProcessedFarm {
  id: number;
  name: string;
  isOnline: boolean;
  lastModified: Date | null;
  gpuCount: number;
  gpus: GpuInfo[];
  hashrate: number;
  power: number;
  uptime: number;
  solutions: number;
  temperature: number;
  raw: FarmResponse;
}

/** Factor to convert a miner's hashrate (in its reported hs_units) to H/s. */
export function hsUnitToHps(unit?: string): number {
  switch ((unit ?? 'hs').toLowerCase()) {
    case 'khs': return 1_000;
    case 'mhs': return 1_000_000;
    case 'ghs': return 1_000_000_000;
    default:    return 1; // 'hs'
  }
}

function processRawFarm(raw: FarmResponse, offlineThreshold: number): ProcessedFarm {
  const validGPUs = raw.hello.params.gpu.filter(
    (g) => g.brand !== 'intel' && g.brand !== 'cpu'
  );

  const statsMtime = raw.statsMtime ? new Date(raw.statsMtime) : null;
  const isOnline = statsMtime
    ? Date.now() - statsMtime.getTime() <= offlineThreshold
    : false;

  const stats = raw.stats?.params;

  // A rig can run several miners at once (e.g. AMD + Nvidia). Each reports its
  // own hashrate in its OWN unit (hs_units differ per miner) — but the rig also
  // gives a per-miner total in kH/s (total_khs, total_khs2). Collect every miner
  // block so hashrate/temps/shares cover all of them, not just the first.
  const minerBlocks = [stats?.miner_stats, stats?.miner_stats2].filter(
    (m): m is NonNullable<typeof m> => m != null,
  );

  // Hashrate, in H/s for formatHashrate. Prefer the rig's own kH/s totals
  // (authoritative, unit-consistent); fall back to summing hs by hs_units.
  let hashrate = 0;
  if (isOnline) {
    const totalKhs =
      (Number(stats?.total_khs) || 0) + (Number(stats?.total_khs2) || 0);
    if (totalKhs > 0) {
      hashrate = totalKhs * 1000;
    } else {
      for (const mb of minerBlocks) {
        const factor = hsUnitToHps(mb.hs_units);
        hashrate += (mb.hs ?? []).reduce((s, h) => s + (h || 0), 0) * factor;
      }
    }
  }

  const power = isOnline
    ? (stats?.power ?? []).filter((v) => v !== 0).reduce((s, v) => s + v, 0)
    : 0;

  const bootTime = raw.hello.params.boot_time;
  const uptime = Math.max(0, Math.floor(Date.now() / 1000) - bootTime);

  // Accepted shares + temperatures across ALL miners on the rig.
  const solutions = isOnline
    ? minerBlocks.reduce((s, mb) => s + (Number(mb.ar?.[0]) || 0), 0)
    : 0;

  const temps = isOnline
    ? minerBlocks.flatMap((mb) => mb.temp ?? []).filter((t) => t > 0)
    : [];
  const temperature =
    temps.length > 0
      ? Math.round(temps.reduce((s, t) => s + t, 0) / temps.length)
      : 0;

  return {
    id: raw.id,
    name: raw.name,
    isOnline,
    lastModified: statsMtime,
    gpuCount: validGPUs.length,
    gpus: validGPUs,
    hashrate,
    power,
    uptime,
    solutions,
    temperature,
    raw,
  };
}

export function useFarms(offlineThreshold: number) {
  const [farms, setFarms] = useState<ProcessedFarm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const raw = await api.getFarms();
      const processed = raw.map((f) => processRawFarm(f, offlineThreshold));
      setFarms(processed);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load farms');
    } finally {
      setLoading(false);
    }
  }, [offlineThreshold]);

  return { farms, loading, error, refresh };
}
